import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Input } from "@/components/ui/input";
import { useServerFn } from "@tanstack/react-start";
import { listEmailLogs, getEmailStats, type EmailLogEntry, type EmailStats } from "@/lib/email-logs.functions";
import { sendBenefitsBroadcast } from "@/lib/send-benefits-broadcast.functions";
import { sendWeeklyEventsBroadcast } from "@/lib/send-weekly-events-broadcast.functions";
import { sendMonthlyEventsBroadcast } from "@/lib/send-monthly-events-broadcast.functions";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, Ban, AlertTriangle, Send, CalendarDays, CalendarRange } from "lucide-react";

export const Route = createFileRoute("/harikaradmin/emails")({
  head: () => ({ meta: [{ title: "E-posta Logları — Admin" }] }),
  component: EmailsPage,
});

function statusBadge(status: string) {
  const map: Record<string, { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
    sent: { label: "Gönderildi", cls: "bg-success/15 text-success", Icon: CheckCircle2 },
    pending: { label: "Bekliyor", cls: "bg-muted text-muted-foreground", Icon: Clock },
    failed: { label: "Hata", cls: "bg-destructive/15 text-destructive", Icon: AlertTriangle },
    dlq: { label: "İptal (max retry)", cls: "bg-destructive/15 text-destructive", Icon: XCircle },
    suppressed: { label: "Engellendi", cls: "bg-warning/15 text-warning", Icon: Ban },
    bounced: { label: "Geri döndü", cls: "bg-destructive/15 text-destructive", Icon: XCircle },
    complained: { label: "Şikayet", cls: "bg-destructive/15 text-destructive", Icon: XCircle },
  };
  const v = map[status] ?? { label: status, cls: "bg-muted text-muted-foreground", Icon: Clock };
  const Icon = v.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${v.cls}`}>
      <Icon className="h-3 w-3" />
      {v.label}
    </span>
  );
}

function EmailsPage() {
  const { user, session, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<EmailLogEntry[]>([]);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [broadcasting, setBroadcasting] = useState(false);
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [weeklySending, setWeeklySending] = useState(false);
  const [weeklyPreview, setWeeklyPreview] = useState<{
    html: string; recipients: number; eventsCount: number;
  } | null>(null);
  const [weeklySubject, setWeeklySubject] = useState("");
  const [monthlyOpen, setMonthlyOpen] = useState(false);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlySending, setMonthlySending] = useState(false);
  const [monthlyPreview, setMonthlyPreview] = useState<{
    html: string; recipients: number; eventsCount: number;
  } | null>(null);
  const [monthlySubject, setMonthlySubject] = useState("");
  const fetchLogs = useServerFn(listEmailLogs);
  const fetchStats = useServerFn(getEmailStats);
  const broadcastBenefits = useServerFn(sendBenefitsBroadcast);
  const broadcastWeekly = useServerFn(sendWeeklyEventsBroadcast);
  const broadcastMonthly = useServerFn(sendMonthlyEventsBroadcast);

  const handleOpenMonthly = async () => {
    if (!session?.access_token) return;
    setMonthlyOpen(true);
    setMonthlyLoading(true);
    setMonthlyPreview(null);
    try {
      const res = await broadcastMonthly({
        data: { accessToken: session.access_token, mode: "preview", daysAhead: 35 },
      });
      if (res.error) {
        toast.error(res.error);
        setMonthlyOpen(false);
      } else {
        setMonthlyPreview({ html: res.previewHtml, recipients: res.recipients, eventsCount: res.eventsCount });
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Önizleme başarısız");
      setMonthlyOpen(false);
    } finally {
      setMonthlyLoading(false);
    }
  };

  const runMonthlySend = async (mode: "send" | "send_missing") => {
    if (!session?.access_token || !monthlyPreview) return;
    if (monthlyPreview.eventsCount === 0) {
      toast.error("Takvimde etkinlik yok");
      return;
    }
    const label = mode === "send_missing"
      ? `Son 10 günde aylık takvimi almamış üyelere gönderilsin mi?`
      : `${monthlyPreview.recipients} onaylı üyeye "Aylık takvim" e-postası gönderilsin mi?`;
    if (!confirm(label)) return;
    setMonthlySending(true);
    try {
      const res = await broadcastMonthly({
        data: {
          accessToken: session.access_token,
          mode,
          daysAhead: 35,
          subjectOverride: monthlySubject.trim() || undefined,
          sinceHours: 240,
        },
      });
      if (res.error) {
        toast.error(res.error);
      } else {
        toast.success(`${res.sent} gönderildi${res.failed ? `, ${res.failed} başarısız` : ""}`);
        setMonthlyOpen(false);
        fetchLogs({ headers: { authorization: `Bearer ${session.access_token}` } })
          .then((rows) => setLogs(rows))
          .catch((e) => console.error(e));
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gönderim başarısız");
    } finally {
      setMonthlySending(false);
    }
  };


  const handleBroadcastBenefits = async () => {
    if (!session?.access_token) return;
    if (!confirm("Tüm onaylı üyelere 'Ayrıcalıkların' e-postası gönderilsin mi?")) return;
    setBroadcasting(true);
    try {
      const res = await broadcastBenefits({ data: { accessToken: session.access_token } });
      if (res.error) {
        toast.error(res.error);
      } else {
        toast.success(`${res.sent} gönderildi${res.failed ? `, ${res.failed} başarısız` : ""}`);
        fetchLogs({ headers: { authorization: `Bearer ${session.access_token}` } })
          .then((rows) => setLogs(rows))
          .catch((e) => console.error(e));
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gönderim başarısız");
    } finally {
      setBroadcasting(false);
    }
  };

  const handleOpenWeekly = async () => {
    if (!session?.access_token) return;
    setWeeklyOpen(true);
    setWeeklyLoading(true);
    setWeeklyPreview(null);
    try {
      const res = await broadcastWeekly({
        data: { accessToken: session.access_token, mode: "preview", daysAhead: 8 },
      });
      if (res.error) {
        toast.error(res.error);
        setWeeklyOpen(false);
      } else {
        setWeeklyPreview({ html: res.previewHtml, recipients: res.recipients, eventsCount: res.eventsCount });
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Önizleme başarısız");
      setWeeklyOpen(false);
    } finally {
      setWeeklyLoading(false);
    }
  };

  const runWeeklySend = async (mode: "send" | "send_missing") => {
    if (!session?.access_token || !weeklyPreview) return;
    if (weeklyPreview.eventsCount === 0) {
      toast.error("Bu hafta için etkinlik yok");
      return;
    }
    const label = mode === "send_missing"
      ? `Son 72 saatte "weekly-events" almamış üyelere gönderilsin mi?`
      : `${weeklyPreview.recipients} onaylı üyeye "Bu haftanın etkinlikleri" e-postası gönderilsin mi?`;
    if (!confirm(label)) return;
    setWeeklySending(true);
    try {
      const res = await broadcastWeekly({
        data: {
          accessToken: session.access_token,
          mode,
          daysAhead: 8,
          subjectOverride: weeklySubject.trim() || undefined,
          sinceHours: 72,
        },
      });
      if (res.error) {
        toast.error(res.error);
      } else {
        toast.success(`${res.sent} gönderildi${res.failed ? `, ${res.failed} başarısız` : ""}`);
        setWeeklyOpen(false);
        fetchLogs({ headers: { authorization: `Bearer ${session.access_token}` } })
          .then((rows) => setLogs(rows))
          .catch((e) => console.error(e));
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gönderim başarısız");
    } finally {
      setWeeklySending(false);
    }
  };

  const handleSendWeekly = () => runWeeklySend("send");
  const handleSendWeeklyMissing = () => runWeeklySend("send_missing");


  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin || !session?.access_token) return;
    setListLoading(true);
    fetchLogs({ data: { status: statusFilter }, headers: { authorization: `Bearer ${session.access_token}` } })
      .then((rows) => setLogs(rows))
      .catch((e) => console.error(e))
      .finally(() => setListLoading(false));
  }, [isAdmin, session?.access_token, fetchLogs, statusFilter]);

  useEffect(() => {
    if (!isAdmin || !session?.access_token) return;
    fetchStats({ headers: { authorization: `Bearer ${session.access_token}` } })
      .then((s) => setStats(s))
      .catch((e) => console.error(e));
  }, [isAdmin, session?.access_token, fetchStats]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((l) => {
      if (!q) return true;
      return (
        l.recipient_email.toLowerCase().includes(q) ||
        (l.member_name?.toLowerCase().includes(q) ?? false) ||
        l.template_name.toLowerCase().includes(q)
      );
    });
  }, [logs, search]);

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-6xl px-4 py-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">E-posta Logları</h1>
          <p className="mt-2 text-muted-foreground">Üyelere giden e-posta denemeleri.</p>
        </div>

        <AdminTabs />

        <div className="mb-6 rounded-2xl border border-border bg-card p-4 shadow-card">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold">Toplu E-posta</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Tüm onaylı üyelere hazır şablonlarla e-posta gönder.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleOpenWeekly} variant="secondary" className="gap-2">
                <CalendarDays className="h-4 w-4" />
                Bu haftanın etkinlikleri
              </Button>
              <Button onClick={handleOpenMonthly} variant="secondary" className="gap-2">
                <CalendarRange className="h-4 w-4" />
                Aylık takvim
              </Button>

              <Button onClick={handleBroadcastBenefits} disabled={broadcasting} className="gap-2">
                <Send className="h-4 w-4" />
                {broadcasting ? "Gönderiliyor..." : "Ayrıcalıkları Gönder"}
              </Button>
            </div>
          </div>
        </div>

        <Dialog open={weeklyOpen} onOpenChange={setWeeklyOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Bu haftanın etkinlikleri — Önizleme</DialogTitle>
            </DialogHeader>
            {weeklyLoading || !weeklyPreview ? (
              <div className="py-16 text-center text-sm text-muted-foreground">Yükleniyor...</div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-border bg-muted/30 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Etkinlik</div>
                    <div className="mt-1 text-xl font-bold">{weeklyPreview.eventsCount}</div>
                  </div>
                  <div className="rounded-xl border border-border bg-muted/30 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Alıcı</div>
                    <div className="mt-1 text-xl font-bold">{weeklyPreview.recipients}</div>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Konu (opsiyonel)</label>
                  <Input
                    value={weeklySubject}
                    onChange={(e) => setWeeklySubject(e.target.value)}
                    placeholder="JUËN Club — Bu hafta neler var? 🎧"
                  />
                </div>
                <div className="overflow-hidden rounded-xl border border-border bg-white">
                  <iframe
                    title="E-posta önizleme"
                    srcDoc={weeklyPreview.html}
                    className="h-[520px] w-full"
                  />
                </div>
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="ghost" onClick={() => setWeeklyOpen(false)}>Kapat</Button>
              <Button
                variant="secondary"
                onClick={handleSendWeeklyMissing}
                disabled={weeklySending || weeklyLoading || !weeklyPreview || weeklyPreview.eventsCount === 0}
                className="gap-2"
                title="Son 72 saatte weekly-events almamış onaylı üyelere gönder"
              >
                <Send className="h-4 w-4" />
                {weeklySending ? "..." : "Eksiklere gönder"}
              </Button>
              <Button
                onClick={handleSendWeekly}
                disabled={weeklySending || weeklyLoading || !weeklyPreview || weeklyPreview.eventsCount === 0}
                className="gap-2"
              >
                <Send className="h-4 w-4" />
                {weeklySending ? "Gönderiliyor..." : "Herkese gönder"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={monthlyOpen} onOpenChange={setMonthlyOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Aylık takvim — Önizleme</DialogTitle>
            </DialogHeader>
            {monthlyLoading || !monthlyPreview ? (
              <div className="py-16 text-center text-sm text-muted-foreground">Yükleniyor...</div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-border bg-muted/30 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Etkinlik</div>
                    <div className="mt-1 text-xl font-bold">{monthlyPreview.eventsCount}</div>
                  </div>
                  <div className="rounded-xl border border-border bg-muted/30 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Alıcı</div>
                    <div className="mt-1 text-xl font-bold">{monthlyPreview.recipients}</div>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Konu (opsiyonel)</label>
                  <Input
                    value={monthlySubject}
                    onChange={(e) => setMonthlySubject(e.target.value)}
                    placeholder="JUËN Club — Aylık takvim 🗓️"
                  />
                </div>
                <div className="overflow-hidden rounded-xl border border-border bg-white">
                  <iframe
                    title="Aylık takvim önizleme"
                    srcDoc={monthlyPreview.html}
                    className="h-[520px] w-full"
                  />
                </div>
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="ghost" onClick={() => setMonthlyOpen(false)}>Kapat</Button>
              <Button
                variant="secondary"
                onClick={() => runMonthlySend("send_missing")}
                disabled={monthlySending || monthlyLoading || !monthlyPreview || monthlyPreview.eventsCount === 0}
                className="gap-2"
                title="Son 10 günde aylık takvimi almamış onaylı üyelere gönder"
              >
                <Send className="h-4 w-4" />
                {monthlySending ? "..." : "Eksiklere gönder"}
              </Button>
              <Button
                onClick={() => runMonthlySend("send")}
                disabled={monthlySending || monthlyLoading || !monthlyPreview || monthlyPreview.eventsCount === 0}
                className="gap-2"
              >
                <Send className="h-4 w-4" />
                {monthlySending ? "Gönderiliyor..." : "Herkese gönder"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>




        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="Toplam" value={stats?.total ?? null} />
          <StatCard label="Gönderildi" value={stats?.sent ?? null} tone="success" />
          <StatCard label="Hata" value={stats?.failed ?? null} tone="destructive" />
          <StatCard label="Engellendi" value={stats?.suppressed ?? null} tone="warning" />
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Input
            placeholder="Üye, e-posta veya şablon ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
          <div className="flex gap-1 rounded-xl border border-border bg-card p-1 shadow-card">
            {[
              { k: "all", l: "Hepsi" },
              { k: "sent", l: "Gönderildi" },
              { k: "failed", l: "Hata" },
              { k: "suppressed", l: "Engellendi" },
            ].map((o) => (
              <button
                key={o.k}
                onClick={() => setStatusFilter(o.k)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  statusFilter === o.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>

        {listLoading ? (
          <div className="py-16 text-center text-muted-foreground">Yükleniyor...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
            Kayıt bulunamadı.
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Üye</th>
                    <th className="px-4 py-3">E-posta</th>
                    <th className="px-4 py-3">Şablon</th>
                    <th className="px-4 py-3">Durum</th>
                    <th className="px-4 py-3">Tarih</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l, i) => (
                    <tr key={(l.message_id ?? i.toString()) + i} className="border-t border-border align-top">
                      <td className="px-4 py-3 font-medium">
                        {l.member_id ? (
                          <Link to="/harikaradmin" className="hover:underline">
                            {l.member_name ?? "—"}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{l.recipient_email}</td>
                      <td className="px-4 py-3"><code className="text-xs">{l.template_name}</code></td>
                      <td className="px-4 py-3">
                        {statusBadge(l.status)}
                        {l.error_message && (
                          <p className="mt-1 max-w-xs text-xs text-destructive">{l.error_message}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {new Date(l.created_at).toLocaleString("tr-TR")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number | null; tone?: "success" | "destructive" | "warning" }) {
  const cls =
    tone === "success" ? "text-success" :
    tone === "destructive" ? "text-destructive" :
    tone === "warning" ? "text-warning" : "text-foreground";
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-card">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${cls}`}>{value === null ? "…" : value.toLocaleString("tr-TR")}</div>
    </div>
  );
}
